import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { Room } from 'models/Room';
import { RootState } from 'store';
import { authFetchBaseQuery, baseUrl } from './apiConfig';
import { ResponseBase } from './request';

const baseEndpointUrl = `${baseUrl}`;
const apiPrefix = 'rooms';

export interface RoomResult {
  createdAt: string;
  fk_unit_id: string;
  id: string;
  identifier: string;
  image: string;
  nick_name: string;
  physical_location: string;
  position: number;
  status: number;
  type: string;
  updatedAt: string;
}

interface RoomsResponse extends ResponseBase {
  error: boolean;
  data: RoomResult[];
}

export const roomApi = createApi({
  reducerPath: 'roomApi',
  baseQuery: authFetchBaseQuery({
    baseUrl: baseEndpointUrl,
  }),
  keepUnusedDataFor: Infinity,
  endpoints: (builder) => ({
    getMyRooms: builder.query<Room[], void>({
      query: () => ({ url: apiPrefix }),
      transformResponse: (response: RoomsResponse) =>
        response.data.map((room) => ({
          createdAt: room.createdAt,
          unitId: room.fk_unit_id,
          id: room.id,
          identifier: room.identifier,
          image: room.image,
          nickName: room.nick_name,
          physicalLocation: room.physical_location,
          position: room.position,
          status: room.status,
          type: room.type,
          updatedAt: room.updatedAt,
        })),
    }),
  }),
});

export const { useGetMyRoomsQuery } = roomApi;

const selectRoomResult = roomApi.endpoints.getMyRooms.select();

export const selectAllRooms = createSelector(
  selectRoomResult,
  (roomsResult) => roomsResult.data ?? [],
);

export const selectRoomById = createSelector(
  selectAllRooms,
  (state: RootState, roomId: string) => roomId,
  (rooms: Room[], roomId: string) => rooms.find((r) => r.id === roomId),
);
